import React, { useState, useContext } from 'react';
import type { IReleaseDropdown } from '../../../models/ReleaseDropdown';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SelectChangeEvent } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../../styles/ButtonTextStyle';
import LocalizationContext from '../../../../shared/contexts/LocalizationContext';
import LocalizationHelper from '../../../../helpers/TranslationHelper';
import type { LocalizationEntity } from '../../../../shared/models/localization';

interface ReleaseModalProps {
  releasesData: IReleaseDropdown[];
  isOpen: boolean;
  onClose: () => void;
  onMove: (selectedOption: number) => void;
}

const releaseModal: React.FC<ReleaseModalProps> = ({ releasesData, isOpen, onClose, onMove }) => {
  const [selectedOption, setSelectedOption] = useState(releasesData[0].releaseId);
  const localization = useContext<Array<LocalizationEntity>>(LocalizationContext);
  const handleOptionSelect = (releaseId: number) => {
    setSelectedOption(releaseId);
  };

  const handleMove = () => {
    onMove(selectedOption);
    onClose();
  };

  const moveText: string = LocalizationHelper.getLocaleEntityValue(
    localization,
    'ZSite.Frontend.Move',
    'Move'
  );

  const selectItems = releasesData.map((release) => (
    <MenuItem value={release.releaseId} key={release.releaseId}>
      {release.releaseVersion}
    </MenuItem>
  ));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth={true}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            maxWidth: '492px',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        Move to Release
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Select
          fullWidth={true}
          value={selectedOption}
          labelId="release-dialog-select-label"
          input={<OutlinedInput />}
          onChange={(e: SelectChangeEvent<number>) => handleOptionSelect(e.target.value as number)}
          size="small"
          className="mb-3"
          sx={{ padding: '0.5% 0 0.5% 0' }}
        >
          {selectItems}
        </Select>
      </DialogContent>
      <ThemeProvider theme={theme}>
        <DialogActions>
          <Button variant="contained" color="info" onClick={handleMove}>
            {moveText}
          </Button>
        </DialogActions>
      </ThemeProvider>
    </Dialog>
  );
};

export const ReleaseModal = React.memo(releaseModal);
